import React from "react";
import Icons from "../icons";

/* What Cortex QA's checks against the page found in what the AppPage QA Agent
   wrote, with no AI call: a scenario against the page's test model (the API's
   app/apppage_qa/verify.py) and a Playwright script against its scenario and
   that model (verify_script.py). A report is {ok, errors, warnings, model,
   findings, tests?}; a finding {level: "error"|"warning", code, message,
   line?, lineno?}. A test written before the checks has no report: nothing is
   shown for it. */

/* Every finding of a report: its own, then each scripted test's. */
export function findingsOf(report) {
  if (!report) return [];
  return [...(report.findings || []), ...(report.tests || []).flatMap((t) => t.findings || [])];
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/* The badge on a test or an Artifact: Checked, N warnings, or N errors
   (after `label` when given: "Script · 2 errors"). */
export function ChecksBadge({ report, what = "scenario", label = "" }) {
  if (!report) return null;
  const errors = report.errors || 0;
  const warnings = report.warnings || 0;
  const [tone, text, icon] = errors
    ? ["bg-rose-50 text-rose-700", plural(errors, "error"), Icons.Alert]
    : warnings
    ? ["bg-amber-50 text-amber-800", plural(warnings, "warning"), Icons.Alert]
    : ["bg-emerald-50 text-emerald-700", "Checked", Icons.ShieldCheck];
  const Icon = icon;
  const title = errors
    ? `The ${what} has ${plural(errors, "error")} against the page's test model: it fails for a reason that is not the page's`
    : warnings
    ? `The ${what} holds against the page's test model, with ${plural(warnings, "warning")} to look at`
    : `The ${what} holds against the page's test model`;
  return (
    <span
      title={title}
      aria-label={`${label || "Checks"}: ${
        errors ? plural(errors, "error") : warnings ? plural(warnings, "warning") : "no problem found"
      }`}
      className={`inline-flex flex-none items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {label ? `${label} · ${text}` : text}
    </span>
  );
}

/* The findings, errors first, each with the step or the script line it is about. */
export function ChecksList({ report, title = "Checks against the page" }) {
  if (!report) return null;
  const findings = findingsOf(report);
  const sorted = [...findings.filter((f) => f.level === "error"), ...findings.filter((f) => f.level !== "error")];
  return (
    <section aria-label={title} className="rounded-lg border border-slate-200 bg-card p-3">
      <p className="mb-1.5 text-[0.6875rem] font-bold uppercase tracking-wider text-slate-500">{title}</p>
      {report.model === false && (
        <p className="mb-1.5 text-[0.75rem] text-slate-500">
          No test model was stored for this page, so the field ids and values were not checked.
        </p>
      )}
      {sorted.length === 0 ? (
        <p className="flex items-center gap-1.5 text-[0.75rem] text-emerald-700">
          <Icons.ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> No problem found.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {sorted.map((f, i) => {
            const error = f.level === "error";
            return (
              <li key={i} className="flex items-start gap-2 text-[0.75rem]">
                <Icons.Alert
                  className={`mt-0.5 h-3.5 w-3.5 flex-none ${error ? "text-rose-600" : "text-amber-500"}`}
                  role="img"
                  aria-label={error ? "Error" : "Warning"}
                />
                <span className="min-w-0">
                  <span className={error ? "text-rose-700" : "text-slate-700"}>{f.message}</span>
                  {f.line && (
                    <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-500">
                      {f.lineno ? `line ${f.lineno}: ` : ""}
                      {f.line}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
